/** Orders: `rule.ticket` — a visitor's order, generated from a menu of
 * courses when it arrives, shown on a ticket card, assembled step by step by
 * pressing placed tools in the ticket's exact order (some steps graded on a
 * timing gauge), then judged: accuracy and speed make its quality, quality
 * makes the tip and the grade; a ruinous mistake or a walk-out fails it — and
 * `rule.gauge`, the timing gauge such a step is graded on. */
import type { JsonObject } from '../../json.js';
import { bool, id, int, list, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';
import type { EntityV1 } from '../world.js';
import { pressableItem } from './pointer.js';
import { patienceLeft } from './patron.js';

export type CourseDef = {
  /** Course name (`flavor`, `topping`, `nails`). */
  id: string;
  /** Items a ticket may name for it (each a placed tool's `item`). */
  options: string[];
  /** How many picks of this course a ticket asks for (repeats allowed). */
  min: number;
  max: number;
  /** Item id of the timing gauge a correct pick of this course is then graded
   * on, or `none`. */
  gauge: string;
  /** A wrong option OF THIS COURSE: ignored (the step stays), ruins the
   * order, or is accepted with zero accuracy. */
  onWrong: 'ignore' | 'ruin' | 'accept';
  /** How a completed pick shows on the dish: not at all, recolours the dish,
   * or stacks a layer in the item's colour. */
  build: 'none' | 'base' | 'layer';
};

export type TicketConfig = {
  courses: CourseDef[];
  /** Orders open while the holder carries this tag (seated). */
  waitTag: string;
  /** A press of a tool from another course than the next step's. */
  outOfOrder: 'ignore' | 'ruin';
  /** The ticket card shown while the order is open. */
  card: { archetype: string; x: number; y: number; title: string };
  /** The dish being assembled (`none` archetype: no dish). */
  dish: { archetype: string; x: number; y: number; layer: string; layerY: number; layerStep: number };
  /** quality = accuracy·mean step accuracy + speed·patience left (weights sum to 1);
   * tip = round(quality·tip·weight); the grade counts `weight` possible points. */
  scoring: { accuracy: number; speed: number; tip: number; weight: number };
  /** A failed order costs the serving seat a life. */
  lifeOnFail: boolean;
};

type Step = { course: number; item: string };

const NONE = 'none';

/** Ticket text for a placed item: the label of the tool that carries it. */
function itemLabel(ctx: ModuleCtx<TicketConfig>, item: string): string {
  const tool = toolFor(ctx, item);
  return tool?.label ?? item.replaceAll('-', ' ').toUpperCase();
}

function toolFor(ctx: ModuleCtx<TicketConfig>, item: string): EntityV1 | undefined {
  return ctx.entities().find(e => ctx.hasModule(e, 'rule.pressable') && !ctx.hasModule(e, 'rule.gauge') && pressableItem(ctx, e) === item);
}

function gaugeFor(ctx: ModuleCtx<TicketConfig>, gaugeId: string): EntityV1 | undefined {
  return ctx.entities().find(e => ctx.peer(e, 'rule.gauge')?.id === gaugeId);
}

/** The open order the tools serve now: the earliest-seated open ticket. */
function activeOrder(ctx: ModuleCtx<TicketConfig>): EntityV1 | undefined {
  return ctx.entities({ status: 'active' }).find(e => ctx.peer(e, 'rule.ticket')?.status === 'open');
}

function progressOf(ctx: ModuleCtx<TicketConfig>, archetype: string): number {
  const progress = (ctx.state.progress ??= {}) as JsonObject;
  return typeof progress[archetype] === 'number' ? progress[archetype] : 0;
}

function addProgress(ctx: ModuleCtx<TicketConfig>, archetype: string, amount: number): void {
  const progress = (ctx.state.progress ??= {}) as JsonObject;
  progress[archetype] = progressOf(ctx, archetype) + amount;
}

function cleanup(ctx: ModuleCtx<TicketConfig>, d: JsonObject): void {
  for (const ref of ['card', 'dish'] as const) {
    const e = ctx.entity(str(d, ref));
    if (e) ctx.despawn(e, 'order-closed');
  }
  for (const layer of d.layers as string[]) {
    const e = ctx.entity(layer);
    if (e) ctx.despawn(e, 'order-closed');
  }
  d.card = null; d.dish = null; d.layers = [];
}

function close(ctx: ModuleCtx<TicketConfig>, e: EntityV1, done: boolean, reason: string): void {
  const c = ctx.config, d = ctx.data(e), slot = read(d, 'slot');
  const steps = d.steps as Step[], accuracy = d.accuracy as number[];
  const at = { x: e.x, y: e.y - e.h / 2 };
  d.status = done ? 'done' : 'failed';
  if (done) {
    const mean = accuracy.reduce((sum, a) => sum + a, 0) / Math.max(1, steps.length);
    const quality = Math.max(0, Math.min(1, c.scoring.accuracy * mean + c.scoring.speed * patienceLeft(ctx, e)));
    const tip = Math.round(quality * c.scoring.tip * c.scoring.weight);
    const perfect = read(d, 'mistakes') === 0;
    ctx.addScore(slot, tip);
    addProgress(ctx, e.archetype, 2);
    ctx.signal('order-done', e, null, { quality, tip, perfect, slot });
    ctx.signal('graded', e, null, { earned: quality * c.scoring.weight, possible: c.scoring.weight });
    ctx.emit('serve', { ...at, slot, points: tip, quality, perfect, kind: e.archetype });
  } else {
    ctx.signal('order-failed', e, null, { reason, slot });
    ctx.signal('graded', e, null, { earned: 0, possible: c.scoring.weight });
    if (reason !== 'walkout') ctx.emit('ruin', { ...at, slot, reason, kind: e.archetype });
    // The life is settled next tick, in the interact stage: lives are read in
    // the rules stage, and rules run kind by kind, so a forfeit raised here
    // would reach a player kind that sorts before this one too late.
    if (c.lifeOnFail) d.owed = slot;
  }
  cleanup(ctx, d);
}

/** One completed step: its accuracy, its look on the dish, the next step. */
function complete(ctx: ModuleCtx<TicketConfig>, e: EntityV1, accuracy: number, tool: EntityV1 | undefined): void {
  const c = ctx.config, d = ctx.data(e), steps = d.steps as Step[];
  const step = steps[read(d, 'index')]!, course = c.courses[step.course]!;
  (d.accuracy as number[]).push(accuracy);
  d.index = read(d, 'index') + 1;
  addProgress(ctx, e.archetype, 1 + accuracy);
  ctx.emit('step', { x: tool?.x ?? e.x, y: (tool ? tool.y - tool.h / 2 : e.y), slot: read(d, 'slot'), item: step.item, accuracy });
  const dish = ctx.entity(str(d, 'dish'));
  if (dish && course.build === 'base') dish.tint = tool?.tint ?? dish.tint;
  if (dish && course.build === 'layer' && c.dish.layer !== NONE) {
    const layers = d.layers as string[];
    const layer = ctx.spawn(c.dish.layer, { x: c.dish.x, y: c.dish.layerY - layers.length * c.dish.layerStep,
      ...(tool?.tint !== undefined && tool.tint !== null ? { tint: tool.tint } : {}) });
    layers.push(layer.id);
  }
  if (read(d, 'index') >= steps.length) close(ctx, e, true, 'served');
}

function cardText(ctx: ModuleCtx<TicketConfig>, d: JsonObject): string {
  const steps = d.steps as Step[], index = read(d, 'index');
  const lines = steps.map((step, i) => `${i < index ? '✓' : i === index ? '▶' : '·'} ${itemLabel(ctx, step.item)}`);
  return [ctx.config.card.title, ...lines].join('\n');
}

export const ticket = defineModule<TicketConfig>({
  id: 'rule.ticket', version: 1, attach: 'entity', role: 'rule',
  summary: 'A seated visitor\'s order from a course menu: shown on a ticket, assembled by pressing tools in its exact order (timing-gauge steps graded on accuracy), judged on accuracy and speed into a tip and a grade; a ruinous mistake or a walk-out fails it.',
  config: obj({
    courses: list(obj({ id: id(), options: list(id(), 8, 1), min: int(0, 8), max: int(1, 8), gauge: id(),
      onWrong: oneOf('ignore', 'ruin', 'accept'), build: oneOf('none', 'base', 'layer') }), 8, 1),
    waitTag: id(), outOfOrder: oneOf('ignore', 'ruin'),
    card: obj({ archetype: id(), x: num(0, 64), y: num(0, 64), title: { type: 'string', maxLength: 24 } }),
    dish: obj({ archetype: id(), x: num(0, 64), y: num(0, 64), layer: id(), layerY: num(0, 64), layerStep: num(0, 4) }),
    scoring: obj({ accuracy: num(0, 1), speed: num(0, 1), tip: int(0, 10000), weight: int(1, 10) }),
    lifeOnFail: bool(),
  }),
  requires: ['rule.pressable', 'ai.patron'], events: ['step', 'wrong', 'serve', 'ruin'],
  presentation: {
    step: { cue: 'collect', vfx: { effect: 'burst', palette: 'slot', count: 6 } },
    wrong: { cue: 'fail', vfx: { effect: 'popup', text: 'NOT THAT ONE' } },
    serve: { cue: 'deliver', vfx: { effect: 'burst', palette: 'slot', count: 14 } },
    ruin: { cue: 'fail', vfx: { effect: 'shake' } },
  },
  proofs: ['ticket-from-menu-counts', 'exact-order', 'wrong-option-ignored', 'wrong-option-ruins', 'wrong-option-accepted-at-zero',
    'out-of-order-policy', 'gauge-step-graded', 'quality-from-accuracy-and-speed', 'tip-and-grade', 'walkout-fails', 'fail-costs-a-life',
    'card-shows-progress', 'dish-builds', 'earliest-order-served-first'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ ...c, card: { ...c.card, archetype: rename(c.card.archetype) },
    dish: { ...c.dish, archetype: c.dish.archetype === NONE ? NONE : rename(c.dish.archetype), layer: c.dish.layer === NONE ? NONE : rename(c.dish.layer) } }),
  check(c, context, archetype) {
    const errors: string[] = [];
    c.courses.forEach((course, i) => { if (course.min > course.max) errors.push(`courses[${i}]: min > max`); });
    if (!c.courses.some(course => course.min > 0)) errors.push('a ticket must ask for at least one pick');
    if (Math.abs(c.scoring.accuracy + c.scoring.speed - 1) > 1e-9) errors.push('scoring accuracy + speed must be 1');
    for (const ref of [c.card.archetype, c.dish.archetype, c.dish.layer]) {
      if (ref === NONE) continue;
      const mods = context.archetypes[ref]?.modules ?? [];
      if (mods.some(m => m !== 'body.static')) errors.push(`${ref} is shown by the ticket: it must carry only body.static`);
    }
    if (c.dish.archetype === NONE && c.courses.some(course => course.build !== 'none')) errors.push('courses build on a dish: name one');
    const mods = archetype ? context.archetypes[archetype]?.modules ?? [] : [];
    if (mods.includes('rule.patience') && mods.indexOf('rule.patience') > mods.indexOf('rule.ticket')) {
      errors.push('rule.patience comes before rule.ticket (a walk-out is read the same tick)');
    }
    return errors;
  },
  hud: () => [{ id: 'served', label: 'SERVED', kind: 'counter' }],
  hudValues(ctx) { return { served: typeof ctx.state.served === 'number' ? ctx.state.served : 0 }; },
  init(ctx, e) {
    const steps: Step[] = [];
    ctx.config.courses.forEach((course, index) => {
      const count = course.min + ctx.randomInt(course.max - course.min + 1);
      for (let k = 0; k < count; k++) steps.push({ course: index, item: course.options[ctx.randomInt(course.options.length)]! });
    });
    Object.assign(ctx.data(e), { steps, index: 0, accuracy: [], mistakes: 0, pending: NONE, slot: 0, status: 'waiting',
      card: null, dish: null, layers: [], owed: -1 });
  },
  systems: {
    interact(ctx, e) {
      if (!e) return;
      const d = ctx.data(e), owed = read(d, 'owed');
      if (owed < 0) return;
      d.owed = -1;
      const player = ctx.entities({ tag: 'player', status: 'any' }).find(p => p.slot === owed);
      if (player) ctx.signal('forfeit', player, e, { reason: 'order-failed' });
    },
    // Rules: after presses (interact) and gauge stops; patience has already
    // signalled a walk-out this tick.
    rules(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      if (d.status === 'waiting' && e.tags.includes(c.waitTag)) {
        d.status = 'open';
        d.card = ctx.spawn(c.card.archetype, { x: c.card.x, y: c.card.y }).id;
        if (c.dish.archetype !== NONE) d.dish = ctx.spawn(c.dish.archetype, { x: c.dish.x, y: c.dish.y }).id;
      }
      if (d.status !== 'open') return;
      if (ctx.signals('walkout').some(s => s.target === e.id)) { close(ctx, e, false, 'walkout'); return; }
      if (activeOrder(ctx)?.id !== e.id) return;
      const steps = d.steps as Step[];
      if (d.pending !== NONE) {
        const stop = ctx.signals('gauge-stop').find(s => s.data.id === d.pending);
        if (!stop) return;
        d.pending = NONE;
        complete(ctx, e, typeof stop.data.accuracy === 'number' ? stop.data.accuracy : 0, toolFor(ctx, steps[read(d, 'index')]!.item));
        return;
      }
      for (const press of ctx.signals('pressed')) {
        const tool = ctx.entity(press.target);
        if (!tool || ctx.hasModule(tool, 'rule.gauge') || typeof press.data.item !== 'string') continue;
        if (typeof press.data.slot === 'number') d.slot = press.data.slot;
        const step = steps[read(d, 'index')]!, course = c.courses[step.course]!;
        if (press.data.item === step.item) {
          if (course.gauge !== NONE) {
            d.pending = course.gauge;
            addProgress(ctx, e.archetype, 0.5);
            const gauge = gaugeFor(ctx, course.gauge);
            if (gauge) ctx.signal('gauge-start', gauge, e, { id: course.gauge });
          } else complete(ctx, e, 1, tool);
          return;
        }
        const sameCourse = course.options.includes(press.data.item);
        const policy = sameCourse ? course.onWrong : c.outOfOrder;
        d.mistakes = read(d, 'mistakes') + 1;
        const at = { x: tool.x, y: tool.y - tool.h / 2, slot: read(d, 'slot'), item: press.data.item };
        if (policy === 'ruin') { close(ctx, e, false, 'wrong'); return; }
        if (policy === 'accept') { complete(ctx, e, 0, tool); return; }
        ctx.emit('wrong', at);
        return;
      }
    },
    flow(ctx, e) {
      if (!e) return;
      if (ctx.signals('order-done').some(s => s.target === e.id)) ctx.state.served = (typeof ctx.state.served === 'number' ? ctx.state.served : 0) + 1;
    },
    present(ctx, e) {
      if (!e) return;
      const d = ctx.data(e), card = ctx.entity(str(d, 'card'));
      if (card) card.label = cardText(ctx, d);
    },
  },
  solverTargets(ctx, e) {
    if (!e || activeOrder(ctx)?.id !== e.id) return [];
    const d = ctx.data(e);
    if (d.pending !== NONE) return [];
    const tool = toolFor(ctx, (d.steps as Step[])[read(d, 'index')]!.item);
    return tool ? [{ x: tool.x, y: tool.y - tool.h / 2, weight: 5, label: 'next-step', press: true }] : [];
  },
  solverProgress: (ctx, archetype) => (archetype === null ? 0 : progressOf(ctx, archetype)),
});

export type GaugeConfig = {
  /** Ticks for one full sweep there and back. */
  periodTicks: number;
  /** Target-zone width range (fraction of the track), seeded per start. */
  zoneMin: number;
  zoneMax: number;
  /** Distance outside the zone at which accuracy reaches 0. */
  falloff: number;
  /** Named scalar speeding the sweep up (a difficulty curve; 1 when unset). */
  speedScalar: string;
};

/** Needle position at sweep tick `t` (0 → 1 → 0 over one period). */
function needleAt(ctx: ModuleCtx<GaugeConfig>, t: number): number {
  const period = ctx.config.periodTicks / Math.max(0.1, ctx.scalar(ctx.config.speedScalar, 1));
  return 0.5 - 0.5 * Math.cos((2 * Math.PI * t) / period);
}

export const gauge = defineModule<GaugeConfig>({
  id: 'rule.gauge', version: 1, attach: 'entity', role: 'rule',
  summary: 'A timing gauge: started by a signal, its needle sweeps back and forth over a seeded target zone; a press locks it and grades accuracy (full inside the zone, falling off outside).',
  config: obj({ periodTicks: int(10, 600), zoneMin: num(0.02, 0.6), zoneMax: num(0.02, 0.6), falloff: num(0.05, 1), speedScalar: id() }),
  requires: ['rule.pressable'], events: ['gauge-hit', 'gauge-miss'],
  presentation: { 'gauge-hit': { cue: 'success', vfx: { effect: 'popup', text: 'PERFECT!' } }, 'gauge-miss': { cue: 'fail' } },
  proofs: ['idle-until-started', 'sweeps-back-and-forth', 'seeded-zone', 'press-locks-and-grades', 'falloff-outside-zone', 'scalar-speeds-sweep', 'meter-shows-needle-and-zone'],
  animStates: () => [],
  check: (c, context, archetype) => {
    const errors = c.zoneMin > c.zoneMax ? ['zoneMin must not exceed zoneMax'] : [];
    const mods = archetype ? context.archetypes[archetype]?.modules ?? [] : [];
    const press = mods.indexOf('rule.pressable');
    if (press < 0 || press > mods.indexOf('rule.gauge')) errors.push('a gauge kind carries rule.pressable before rule.gauge (its presses are read the same tick)');
    return errors;
  },
  init(ctx, e, params) {
    Object.assign(ctx.data(e), { id: typeof params.item === 'string' ? params.item : e.archetype, active: false, t: 0, value: 0, lo: 0, hi: 0 });
  },
  systems: {
    think(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      if (d.active !== true) return;
      d.t = read(d, 't') + 1;
      d.value = needleAt(ctx, read(d, 't'));
    },
    interact(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      if (d.active !== true) return;
      const press = ctx.signals('pressed').find(s => s.target === e.id);
      if (!press) return;
      const value = read(d, 'value'), lo = read(d, 'lo'), hi = read(d, 'hi');
      const outside = value < lo ? lo - value : value > hi ? value - hi : 0;
      const accuracy = Math.max(0, 1 - outside / c.falloff);
      d.active = false;
      ctx.signal('gauge-stop', e, ctx.entity(press.source) ?? null, { id: str(d, 'id')!, accuracy });
      ctx.emit(outside === 0 ? 'gauge-hit' : 'gauge-miss', { x: e.x, y: e.y - e.h / 2, accuracy, slot: typeof press.data.slot === 'number' ? press.data.slot : 0 });
    },
    flow(ctx, e) {
      if (!e || !ctx.signals('gauge-start').some(s => s.target === e.id)) return;
      const c = ctx.config, d = ctx.data(e);
      const width = c.zoneMin + ctx.random() * (c.zoneMax - c.zoneMin);
      const lo = 0.1 + ctx.random() * (0.8 - width);
      Object.assign(d, { active: true, t: 0, value: 0, lo, hi: lo + width });
    },
    present(ctx, e) {
      if (!e) return;
      const d = ctx.data(e);
      e.meter = d.active === true ? { value: read(d, 'value'), zone: [read(d, 'lo'), read(d, 'hi')] } : null;
    },
  },
  solverTargets(ctx, e) {
    if (!e) return [];
    const d = ctx.data(e);
    if (d.active !== true) return [];
    // A press made now is graded on the NEXT tick's needle (the needle moves
    // before presses are read): publish when that one lands in the zone, or
    // when the needle would otherwise jump clean over a narrow zone.
    const lo = read(d, 'lo'), hi = read(d, 'hi'), t = read(d, 't');
    const next = needleAt(ctx, t + 1), after = needleAt(ctx, t + 2);
    const lands = next >= lo && next <= hi;
    const skips = (next < lo && after > hi) || (next > hi && after < lo);
    return lands || skips ? [{ x: e.x, y: e.y - e.h / 2, weight: 8, label: 'stop-in-zone', press: true }] : [];
  },
});
